/**
 * Admin → Legal → Aceptaciones
 * Historial de aceptaciones por tenant y documento.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LegalBackLink } from "@/components/legal/legal-admin-shared";

export const Route = createFileRoute("/admin/ops/legal/acceptances")({
  component: LegalAcceptancesPage,
  head: () => ({ meta: [{ title: "Aceptaciones Legales — Admin" }] }),
});

interface Acceptance {
  id: string;
  tenantId: string;
  userId: string;
  documentId: string;
  documentVersionId: string;
  contentHash: string;
  acceptedAt: string;
  context: string | null;
  evidence: Record<string, unknown>;
}

function LegalAcceptancesPage() {
  const [acceptances, setAcceptances] = useState<Acceptance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/legal/accept")
      .then((r) => r.json())
      .then((d: { history?: Acceptance[] }) => {
        setAcceptances(d.history ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="p-6">
      <LegalBackLink />
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Aceptaciones</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Registro inmutable de aceptaciones. No se pueden eliminar ni modificar.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial ({acceptances.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <p className="px-4 py-3 text-sm text-muted-foreground">Cargando…</p>}
          {!loading && acceptances.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Sin aceptaciones registradas.
            </p>
          )}
          {!loading && acceptances.map((a) => (
            <div key={a.id} className="flex items-start justify-between border-t border-border px-4 py-3">
              <div className="min-w-0 space-y-0.5">
                <p className="truncate font-mono text-xs text-foreground">{a.id}</p>
                <p className="text-xs text-muted-foreground">
                  Tenant: <span className="font-medium">{a.tenantId}</span>
                  {" · "}Usuario: <span className="font-medium">{a.userId}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Doc: {a.documentId.slice(0, 16)}… · Ver: {a.documentVersionId.slice(0, 16)}…
                </p>
              </div>
              <div className="ml-4 flex flex-col items-end gap-1">
                {a.context && (
                  <Badge variant="secondary" className="text-xs">{a.context}</Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {new Date(a.acceptedAt).toLocaleString("es-AR")}
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          Este endpoint devuelve las aceptaciones del tenant autenticado. Para ver todas (Admin),
          se necesita el endpoint <code>/api/admin/legal/acceptances</code> (LEGAL-GAP-07).
        </p>
      </div>
    </div>
  );
}
